import { marked, Renderer } from 'marked';

/**
 * Hardened Markdown rendering for the portal Docs surface (security review
 * 2026-07-09, P3). Docs come from repository/filesystem sources, so this is
 * not tenant-input XSS — but `marked.parse` output was rendered straight into
 * dangerouslySetInnerHTML, giving any compromised doc source or accidental
 * raw-HTML commit a script-execution path in the Portal origin.
 *
 * marked 12 removed its built-in `sanitize` option, and DOMPurify is not a
 * portal dependency, so we neutralise the two concrete vectors at the
 * renderer: raw HTML is escaped (a stray <script> shows as text, never runs),
 * and link hrefs are restricted to safe schemes. All Markdown-generated
 * formatting (headings, lists, code, safe links) renders normally.
 */

const escapeHtml = (value: string): string =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const SAFE_HREF = /^(?:https?:|mailto:|#|\/)/i;

const safeHref = (href: string | null | undefined): string => {
  const candidate = String(href ?? '').trim();
  return SAFE_HREF.test(candidate) ? candidate : '#';
};

const buildSafeRenderer = (): Renderer => {
  const renderer = new Renderer();

  // Never emit raw HTML that appeared in the Markdown source — escape it so a
  // stray <script>/<img onerror> renders as visible text, never live markup.
  renderer.html = (html: string): string => escapeHtml(html);

  // Restrict link hrefs to safe schemes; the text is already rendered by the
  // parser, so delegate to the default link renderer with a cleaned href.
  const originalLink = renderer.link.bind(renderer);
  renderer.link = (href: string, title: string | null | undefined, text: string): string =>
    originalLink(safeHref(href), title, text);

  return renderer;
};

export const renderMarkdownSafe = (content: string): string => {
  const result = marked.parse(content ?? '', {
    gfm: true,
    breaks: true,
    renderer: buildSafeRenderer()
  });
  // With no async extensions configured, marked.parse is synchronous.
  return typeof result === 'string' ? result : '';
};
